import * as React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Alert,
    Stack,
    Box
} from "@mui/material";
import { Localized } from "@fluent/react";
import { format } from "date-fns";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { useAppDispatch } from "../../../app/hooks";
import { showError, showSuccess } from "../../notifications/notificationsSlice";

import { ComputerizedSystem, updateComputerizedSystemValidation } from "../computerizedSystemsApi";
import { ObjectValidationStatus } from "../../project/validationProcess/validationProcessApi";

export const EditComputerizedSystemValidationDialog = ({
    computerizedSystem,
    onCancel,
    onUpdate,
}: {
    computerizedSystem: ComputerizedSystem;
    onCancel: () => void;
    onUpdate: (updated: ComputerizedSystem) => void;
}) => {
    const dispatch = useAppDispatch();

    // helpers do dat
    const toInputDate = (d: Date | string | null | undefined) => {
        if (!d) return "";
        const dt = typeof d === "string" ? new Date(d) : d;
        if (Number.isNaN(dt.getTime())) return "";
        return format(dt, "yyyy-MM-dd");
    };
    const fromInputDate = (s: string) => (s ? new Date(s) : null);
    const toApiDate = (d: Date | null) => (d ? format(d, "yyyy-MM-dd") : null);

    // stan
    const [validationStatus, setValidationStatus] = React.useState<ObjectValidationStatus>(
        (computerizedSystem as any).validationStatus ?? "NOT_APPLICABLE"
    );
    const [validationDate, setValidationDate] = React.useState<Date | null>(
        computerizedSystem && (computerizedSystem as any).validationDate
            ? fromInputDate(toInputDate((computerizedSystem as any).validationDate))
            : null
    );
    const [nextReviewDate, setNextReviewDate] = React.useState<Date | null>(
        computerizedSystem && (computerizedSystem as any).nextReviewDate
            ? fromInputDate(toInputDate((computerizedSystem as any).nextReviewDate))
            : null
    );
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState<string | null>(null);

    const handleSave = async () => {
        setServerError(null);
        setSaving(true);
        try {
            const res = await dispatch(
                updateComputerizedSystemValidation({
                    id: computerizedSystem.id,
                    validationStatus,
                    validationDate: toApiDate(validationDate),
                    nextReviewDate: toApiDate(nextReviewDate)
                })
            );
            if (updateComputerizedSystemValidation.fulfilled.match(res)) {
                const updated = res.payload as ComputerizedSystem;
                dispatch(showSuccess("saved"));
                onUpdate(updated);
            } else {
                let errorMsg = "error";
                if (res.payload && (res.payload as any).kind === "http") {
                    const problem = (res.payload as any).problem;
                    if (problem?.title) errorMsg = problem.title;
                }
                setServerError(errorMsg);
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: (t) => t.palette.grey[100],
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                }}
            >
                <Localized id="cs-validation-edit">Edytuj walidację i zgodność</Localized>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box sx={{ width: "100%", maxWidth: 360 }}>
                        <TextField
                            select
                            fullWidth
                            label={<Localized id="cs-validation-status">Status walidacji</Localized>}
                            value={validationStatus}
                            onChange={(e) => setValidationStatus(e.target.value as ObjectValidationStatus)}
                            disabled={saving}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            minWidth: 240,
                                            "& .MuiMenuItem-root": { py: 0.5, px: 1.5, minHeight: "unset" },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="NOT_APPLICABLE">Nie dotyczy</MenuItem>
                            <MenuItem value="IN_PROGRESS">W toku</MenuItem>
                            <MenuItem value="VALIDATED">Zwalidowany</MenuItem>
                            <MenuItem value="REVALIDATION_REQUIRED">Wymagana rewalidacja</MenuItem>
                            <MenuItem value="NOT_VALIDATED">Nie zwalidowany</MenuItem>
                        </TextField>
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ width: "100%" }} useFlexGap>
                        <Box sx={{ flex: 1, minWidth: 220 }}>
                            <TextField
                                fullWidth
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                label={<Localized id="cs-validation-date">Data walidacji</Localized>}
                                value={toInputDate(validationDate)}
                                onChange={(e) => setValidationDate(fromInputDate(e.target.value))}
                                disabled={saving}
                            />
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 220 }}>
                            <TextField
                                fullWidth
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                label={<Localized id="cs-next-review-date">Następny przegląd</Localized>}
                                value={toInputDate(nextReviewDate)}
                                onChange={(e) => setNextReviewDate(fromInputDate(e.target.value))}
                                disabled={saving}
                            />
                        </Box>
                    </Stack>
                </Stack>

                {serverError && (
                    <Alert sx={{ mt: 1 }} onClose={() => setServerError(null)} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
